import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useWatch } from 'react-hook-form';
import { DNABox, DNAButton, DNAText, DNAContextMenu, DNADivider } from '@alucio/lux-ui/src';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { HubSectionType } from '@alucio/aws-beacon-amplify/src/models';
// DND
import { DNARearranger } from 'src/components/DnD/DNARearranger';
import { arrayMove } from '@dnd-kit/sortable';
import { defaultWidgetsValue, useHubsState } from 'src/state/context/Hubs/HubsStateProvider.proxy';
import { useHubRHFromState } from './useHubRHFormStateProvider';
import { useTenant } from 'src/state/redux/selector/tenant';
import ActiveUser from 'src/state/global/ActiveUser';
import { getWidgetFormName } from './useHubForm';
import { getSectionNavigationName } from './hubSections';
import { getAvailableWidgetOptions } from './widgetOptions';

const styles = StyleSheet.create({
  widgetEditorContainer: {
    width: 240,
    backgroundColor: colors['color-text-white'],
    borderRightColor: colors['color-gray-80'],
    borderRightWidth: 1,
  },
  addSectionBtnContainer: {
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderBottomColor: colors['color-gray-80'],
    borderBottomWidth: 1,
  },
  draggableWidgetCard: {
    paddingVertical: 15,
    paddingRight: 12,
  },
  widgetCardOverlay: {
    borderTopColor: colors['color-gray-80'],
    borderBottomColor: colors['color-gray-80'],
    borderTopWidth: 1,
    borderBottomWidth: 1,
  },
  contextMenuItems: {
    minWidth: 160,
    right: 66,
  },
});

type WidgetContextMenuItem = {
  id: HubSectionType,
  label: string,
  icon: string,
}

export const widgetContextMenu: Record<HubSectionType, WidgetContextMenuItem> = {
  TEXTAREA: {
    id: HubSectionType.TEXTAREA,
    label: 'Text Box',
    icon: 'text-t',
  },
  SHARED_FILES: {
    id: HubSectionType.SHARED_FILES,
    label: 'Shared Files',
    icon: 'user-square',
  },
  TODO_LIST: {
    id: HubSectionType.TODO_LIST,
    label: 'To-Do List',
    icon: 'list-bullets',
  },
  LINKS: {
    id: HubSectionType.LINKS,
    label: 'Links',
    icon: 'link',
  },
}

type WidgetContextMenuProp = {
  type?: HubSectionType,
  id?: string,
}

const WidgetContextMenu: React.FC<WidgetContextMenuProp> = ({ type, id }) => {
  const { handleDeleteWidget } = useHubsState()
  const { rhForm } = useHubRHFromState()
  return (
    <DNAContextMenu style={ styles.contextMenuItems }>
      <DNAContextMenu.Anchor>
        <DNAButton
          testID={`hub-left-side-bar-${type?.toLowerCase()}-ellipsis-button`}
          appearance="ghost"
          status="subtle"
          iconRight="dots-three-vertical-bold"
        />
      </DNAContextMenu.Anchor>
      <DNAContextMenu.Items>
        <DNAContextMenu.Item
          collapseOnPress
          delay={100} // Workaround because sometimes the item deletes too fast before the ContextMenu can hide itself
          title="Delete"
          status="danger"
          iconStatus="danger"
          icon="trash"
          onPress={() => (id && type) && handleDeleteWidget(type, rhForm, id)}
        />
      </DNAContextMenu.Items>
    </DNAContextMenu>
  )
}

type WidgetNavigationLabelProps = {
  widgetId: string,
  widgetType: HubSectionType,
}

const WidgetNavigationLabel: React.FC<WidgetNavigationLabelProps> = ({ widgetId, widgetType }) => {
  const { rhForm } = useHubRHFromState()
  const sectionHeader = useWatch({
    control: rhForm.control,
    name: `${getWidgetFormName(widgetId)}.sectionHeader` as const,
  })

  return (
    <>
      {getSectionNavigationName(sectionHeader, widgetContextMenu[widgetType].label)}
    </>
  )
}

/**
 * used to check if widget is disabled
 * @returns @param widgetType return true if widget is disabled
 */
export const useDisabledWidgets = () => {
  const tenant = useTenant(ActiveUser.user?.tenantId || '');
  const disabledSections = useMemo(() => tenant?.tenant.config.hubsConfig.disabledSections || [], [tenant])

  const isDisabled = (widget: HubSectionType | keyof typeof HubSectionType) => {
    const widgetType = typeof widget === 'string' ? HubSectionType[widget] : widget
    return disabledSections.includes(widgetType)
  }

  return [
    isDisabled,
  ]
}

const LeftSideBar: React.FC = () => {
  const { widgets, setWidgets, items, setItems, handleAddWidget } = useHubsState()
  const { rhForm } = useHubRHFromState()
  const [isWidgetDisabled] = useDisabledWidgets()

  const availableWidgets = getAvailableWidgetOptions(
    Object.values(widgetContextMenu),
    isWidgetDisabled,
  )

  const [activeItemId, setActiveItemId] = useState()
  const activeWidget = useMemo(() => widgets.find(widget => widget.id === activeItemId), [activeItemId])

  useEffect(() => {
    setWidgets(pre => pre.map(widget => ({ ...widget, order: items.indexOf(widget.id) })))
    // update all widgets order in rhForm
    const formValues = rhForm.getValues()
    items.forEach((id, index) => {
      const widgetsType = widgets.find(widget => widget.id === id)?.type
      if (widgetsType) {
        const formName = getWidgetFormName(id)
        const values = formValues.widgets[id] || defaultWidgetsValue[widgetsType]
        // Only update the form if the order has changed
        if (values.order !== index) {
          const payload = { ...values, order: index }
          rhForm.setValue(formName, payload, { shouldDirty: true })
        }
      }
    })
  }, [items])

  function handleDragStart(event) {
    const { active } = event
    if (active.id) {
      setActiveItemId(active.id)
    }
  }

  function handleDragEnd(event) {
    const { active, over } = event
    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
    setActiveItemId(undefined)
  }

  return (
    <DNABox appearance="col" style={ styles.widgetEditorContainer }>
      {/* Add Widget Button */}
      <DNABox style={styles.addSectionBtnContainer}>
        <DNAContextMenu>
          <DNAContextMenu.Anchor>
            <DNAButton
              testID="hub-add-section-button"
              size="lg"
              iconLeft="plus"
              stretch
              disabled={!availableWidgets.length}
            >
              Add section
            </DNAButton>
          </DNAContextMenu.Anchor>
          <DNAContextMenu.Items>
            {availableWidgets.map(widget => (
              <DNAContextMenu.Item
                key={widget.id}
                minWidth={215}
                collapseOnPress
                delay={100}
                icon={widget.icon}
                title={widget.label}
                onPress={() => handleAddWidget(widget.id, rhForm)}
              />
            ))}
          </DNAContextMenu.Items>
        </DNAContextMenu>
      </DNABox>
      {/* Widget Drag and Drop Section */}
      <DNABox fill appearance="col">
        <DNARearranger
          collisionDetection={'closestCenter'}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={['restrictToVerticalAxis']}
          items={items}
          strategy={'verticalListSortingStrategy'}
          overlay={ activeWidget && (
            <DNARearranger.SortableItem
              id={activeWidget.id}
              itemId={activeWidget.id}
              containerId="base"
              handle
              boxContainerStyle={styles.widgetCardOverlay}
            >
              <DNABox fill style={ styles.draggableWidgetCard } alignY="center" spacing="between">
                <DNAText>
                  <WidgetNavigationLabel widgetId={activeWidget.id} widgetType={activeWidget.type as HubSectionType}/>
                </DNAText>
                <WidgetContextMenu />
              </DNABox>
            </DNARearranger.SortableItem>
          )}
        >
          {items.map((id) => {
            const widget = widgets.find(widget => widget.id === id)
            if (!widget) return null
            return (
              <React.Fragment key={id}>
                <DNARearranger.SortableItem
                  id={id}
                  itemId={id}
                  containerId="base"
                  handle
                  testID={`left-side-bar-widget-${widget.type}`}
                >
                  <DNABox fill style={ styles.draggableWidgetCard } alignY="center" spacing="between">
                    <DNAText>
                      <WidgetNavigationLabel widgetId={widget.id} widgetType={widget.type as HubSectionType}/>
                    </DNAText>
                    <WidgetContextMenu type={widget.type as HubSectionType} id={id} />
                  </DNABox>
                </DNARearranger.SortableItem>
                <DNADivider />
              </React.Fragment>
            )
          })}
        </DNARearranger>
      </DNABox>
    </DNABox>
  )
}

export default LeftSideBar
